import { usePayPalScriptReducer } from '@paypal/react-paypal-js';
import {
  createPaypalExpressOrder,
  initPayPalExpressPayment,
  PaypalExpressCreateOrderPayload,
} from 'api/checkout';
import { useAuth } from 'contexts/auth';
import { useCart } from 'contexts/cart';
import { useCheckout } from 'hooks/checkout';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { showErrorToast } from 'utils/toasts';

interface PaypalExpressButtonState {
  loading: boolean;
  createPaypalOrder: () => Promise<string>;
  onPaypalApprove: () => Promise<void>;
}

/**
 * Hook to handle the state of the PayPal Express Button
 * according to the requirement of the react-paypal-js library
 * @see https://github.com/paypal/react-paypal-js
 * @see https://petsdeli.atlassian.net/wiki/spaces/DOKUMENTAT/pages/2015821825/PayPal+Express+Checkout
 */
const usePaypalExpressButton = (): PaypalExpressButtonState => {
  const [{ isPending }] = usePayPalScriptReducer();
  const { push } = useRouter();
  const { customer } = useAuth();
  const { checkout } = useCheckout({
    customer,
  });
  const loadingRef = useRef(false);
  const { effectiveCart } = useCart();

  const requestIdRef = useRef<string>();
  const { isReady, query } = useRouter();

  useEffect(() => {
    if (isReady && query?.requestId) requestIdRef.current = query.requestId as string;
  }, [isReady, query]);

  // To avoid an issue that might be related to the stale closure in the createOrder function,
  // we are using a ref to store the effective cart.
  const effectiveCartRef = useRef(effectiveCart);
  effectiveCartRef.current = effectiveCart;

  const createPaypalOrder = useCallback(async (): Promise<string> => {
    const effectiveCart = effectiveCartRef.current;
    if (!checkout.id || !effectiveCart) {
      throw new Error('Checkout is not defined');
    }

    const payload: PaypalExpressCreateOrderPayload = {
      cart: {
        customerId: customer?.id,
        discount: effectiveCart.discount,
        items: effectiveCart.items.map((item) => ({
          quantity: item.quantity,
          variantId: item.variantId,
        })),
        shipping: effectiveCart.shipping,
      },
      checkoutId: checkout.id,
      listParams: {
        cancelUrl: `${location.protocol}//${location.host}/cart`,
        returnUrl: `${location.protocol}//${location.host}/checkout/express`,
        summaryUrl: `${location.protocol}//${location.host}/checkout/express`,
      },
      method: 'paypal',
    };

    try {
      return await createPaypalExpressOrder({
        payload,
      }).then(({ paypalOrderId, requestId }) => {
        if (!paypalOrderId || !requestId) {
          throw new Error('PayPal order ID or request ID is not defined');
        }
        requestIdRef.current = requestId;
        return paypalOrderId;
      });
    } catch (error) {
      console.error(error);
      showErrorToast({
        error: 'error:unknown',
        caller: 'usePaypalExpressButton',
      });
      return Promise.reject('Error occurred while creating PayPal order');
    }
  }, [checkout, customer?.id, effectiveCartRef]);

  const onPaypalApprove = useCallback(async () => {
    loadingRef.current = true;
    try {
      if (requestIdRef.current) {
        loadingRef.current = false;
        await initPayPalExpressPayment(requestIdRef.current).then(({ redirectUrl }) =>
          push(redirectUrl)
        );
      }
    } catch (error) {
      console.error(error);
      showErrorToast({
        error: 'error:unknown',
        caller: 'usePaypalExpressButton',
      });
    }
  }, [requestIdRef, push]);

  return {
    loading: loadingRef.current || isPending,
    createPaypalOrder,
    onPaypalApprove,
  };
};

export default usePaypalExpressButton;
